import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, ArrowUpDown, Bot, ChevronDown, ChevronLeft, ChevronRight, CircleCheck, CircleDot,
  Download, Eye, FileText, Hourglass, Inbox, ListFilter, Plus, Search, Settings2, TriangleAlert, Truck,
  UserPlus, UserRound, X,
} from 'lucide-react';
import './TicketTableDashboard.css';
import { ALL_STATUS_MAPPING, EDN_STATUS_MAPPING, getStatusLabel as getCanonicalStatusLabel } from '../constants/ticketStatuses';
import { ConfirmDialog, EmptyState, Meter, Segmented, Tabs } from '../design/ui';
import TicketExportModal from './TicketExportModal';
import DisplayPreferences from './DisplayPreferences';
import TicketSummaryOverlay from './TicketSummaryOverlay';
import {
  ChannelMark, MoodInline, TL_NS, clientIdentity, fmtFr, formatParisDateTime, initialsOf, statusTone,
} from './TicketListsShared';
import { parseUtc } from '../utils/dates';
import DashboardPipeline from './DashboardPipeline';
import { fetchPreferences, orderFor, ORDER_ALL } from '../utils/preferences';
import { assigneeName, assignmentTooltipText } from '../utils/assignmentReason';
import { topicBadge } from '../utils/topicBadge';

const getStoredUserData = () => {
  try {
    const storedUserData = localStorage.getItem('userData') || localStorage.getItem('user_data');
    return storedUserData ? JSON.parse(storedUserData) : null;
  } catch (error) {
    return null;
  }
};

const EDN_STATUS_OPTIONS = EDN_STATUS_MAPPING.map(({ value, name }) => ({ value, label: name }));

// The status tiles above the list: one per counter GET /api/tickets/stats
// returns (never a tile for a field it does not return). A tile filters the
// list on the statuses it counts — « Attente transporteur » counts both the
// operational and the commercial carrier waits, so it filters on both.
const STATUS_TILES = [
  { key: 'new', field: 'new_tickets', statuses: ['new'], icon: CircleDot, tone: 'info' },
  { key: 'client', field: 'waiting_client_response', statuses: ['waiting_client_response'], icon: UserRound, tone: 'warning' },
  {
    key: 'carrier', field: 'waiting_carrier_response',
    statuses: ['waiting_carrier_response', 'waiting_carrier_commercial_response'], icon: Truck, tone: 'warning',
  },
  // Legacy « pending » status: a tile only while some ticket still carries it.
  { key: 'pending', field: 'pending_tickets', statuses: ['pending'], icon: Hourglass, tone: 'neutral', onlyWhenAny: true },
];

const sameSet = (a, b) => a.length === b.length && a.every((value) => b.includes(value));
const isCount = (value) => typeof value === 'number' && Number.isFinite(value);

// Thresholds calibrated on the real score distribution (mostly 30–70):
// ≤39 reads as a problem, 40–65 as neutral, >65 as good.
const moodTone = (score) => {
  if (score <= 39) return 'danger';
  if (score <= 65) return undefined;
  return 'success';
};

// Column widths (kit § Tableaux: the whole column, padding included), sized
// on the content they hold, measured at 1 440 px on long French data:
// - status 228: the badge over « who holds it · since when ». Badges are
//   198 px for « Litige en cours de traitement » (EDN's frequent long label,
//   one line), 240–265 px for the three longest, which wrap inside the badge;
// - carrier 136: « Mondial Relay » over a 13-character tracking number;
// - client: the name over « humeur · canal · e-mail » — 216 px at least,
//   then its share of the card as it widens (the e-mail is the longest text);
// - the subject takes the rest, never below SUBJECT_MIN.
// Every mode adds up to 900 px: the width the list gets at 1 280 px, even
// with a classic 17 px scrollbar — no sideways scroll at 1 280 nor 1 440.
const COLUMN_WIDTHS = { check: 44, client: 216, status: 228, carrier: 136, actions: 76 };
// 24 % of a table never narrower than 900 px: ≥ 216 px, wider as the card grows.
const CLIENT_WIDTH_CSS = '24%';
const SUBJECT_MIN = 200;

const TicketTableDashboard = ({
  tickets,
  assignedTickets,
  user,
  onTicketClick,
  compact = false, // kept for callers (Tickets.js); the layout no longer differs
  hasFaAccount = false,
  onCreateTicket,
  brands = [],
  selectedBrand = null,
  onBrandChange = null,
  pagination,
  assignedPagination,
  onPageChange,
  onFilterChange,
  onSortChange = null,
  currentViewMode = 'unassigned',
  onSearchStateChange = null,
  ticketStats = null,
  statsLoading = false,
  attentionReason = null,
  onAttentionReasonChange = null,
  onRefresh = null,
  onNotify = null,
  isLoading = false,
  // dashboard-insights `pipeline`: undefined while it loads, null when the
  // account has none (then the /stats tiles stay).
  pipeline = null
}) => {
  const { t } = useTranslation();
  const tl = (key, fallback, options) => t(`${TL_NS}.${key}`, fallback, options);
  const [searchParams, setSearchParams] = useSearchParams();
  const storedUserData = getStoredUserData();
  const isEdnCustomUser = user?.custom_user === 'edn'
    || user?.user_data?.custom_user === 'edn'
    || storedUserData?.custom_user === 'edn';

  // Initialize state from URL parameters (deep links, back button).
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [sortField, setSortField] = useState(searchParams.get('sortField') || 'updated_at');
  // One or more statuses (?statusFilter=a&statusFilter=b); a single value is
  // the historical shape and still reads the same.
  const [statusFilters, setStatusFilters] = useState(searchParams.getAll('statusFilter').filter(Boolean));
  // « Dormants » of the pipeline: status unchanged for more than N days
  // (search `status_unchanged_days`), kept in the URL like the statuses.
  const [dormantDays, setDormantDays] = useState(() => Number(searchParams.get('dormantDays')) || null);
  const [ednAdminFilter, setEdnAdminFilter] = useState(searchParams.get('ednAdminFilter') || '');
  const [searchFilter, setSearchFilter] = useState(searchParams.get('searchFilter') || '');
  const [ticketViews, setTicketViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  // The person's own display preferences: default view, order per status (28/10).
  const [preferences, setPreferences] = useState(null);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  // Bulk selection + row actions (assign, resolve)
  const [selectedIds, setSelectedIds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  // The open assign menu: { ids, x, y } (fixed: the table scroll box would clip it).
  const [assignMenu, setAssignMenu] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [resolveIds, setResolveIds] = useState(null);
  const statusMenuRef = useRef(null);
  const assignMenuRef = useRef(null);

  // Ticket export is a tenant-owner capability: hidden for employees
  // (the backend enforces 403 regardless).
  const exportRole = user?.role || user?.user_data?.role || storedUserData?.role;
  const canExportTickets = Boolean(exportRole) && !['employee', 'experience'].includes(exportRole);
  const canManageViews = exportRole === 'admin';

  // Set default active tab based on user type, but check URL first
  const getDefaultActiveTab = () => {
    const urlTab = searchParams.get('activeTab');
    if (urlTab) return urlTab;

    return 'General';
  };

  const [activeTopicTab, setActiveTopicTab] = useState(getDefaultActiveTab());
  const [searchResults, setSearchResults] = useState(null);
  // Paged search (KI-111): the backend answers page/limit + total, so a status
  // filter matching 593 tickets pages through them instead of stopping at 200.
  const [searchPagination, setSearchPagination] = useState(null);
  const lastSearchCriteriaRef = useRef(null);
  const SEARCH_PAGE_SIZE = 100;
  const [hasSyncedInitialTab, setHasSyncedInitialTab] = useState(false);

  const ASSIGNED_TAB = 'Assigned';
  const MY_ASSIGNED_TAB = 'Assigned to me';
  const GENERAL_TAB = 'General';
  const CHATBOT_TAB = 'chatbot';
  const VIP_TAB = 'VIP';
  const QUEUE_TABS = [GENERAL_TAB, ASSIGNED_TAB, MY_ASSIGNED_TAB, VIP_TAB];

  const getViewId = (view) => view?.id || view?._id || view?.view_id;
  const buildViewTabKey = (viewId) => `view:${viewId}`;
  const isViewActive = Boolean(activeViewId || (activeTopicTab && activeTopicTab.startsWith('view:')));
  // The attention chips count tickets across BOTH lists (the backend applies
  // the attention_reason filter to the unassigned AND assigned queries), so
  // the facet view merges them exactly like a saved view — otherwise « 18 »
  // on the chip showed 2 rows because 16 were assigned (KI-138).
  const isMergedView = isViewActive || Boolean(attentionReason);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setShowStatusMenu(false);
      }
      if (assignMenuRef.current && !assignMenuRef.current.contains(event.target)) {
        setAssignMenu(null);
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setShowStatusMenu(false);
        setAssignMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  // A fixed menu does not follow the page: close it when the page scrolls.
  useEffect(() => {
    if (!assignMenu) return undefined;
    const close = () => setAssignMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [assignMenu]);

  // Selection only ever references rows that are on screen.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.length) return prev;
      const visible = new Set([
        ...(tickets || []).map((item) => item._id),
        ...(assignedTickets || []).map((item) => item._id),
        ...((searchResults || []).map((item) => item._id)),
      ]);
      const kept = prev.filter((id) => visible.has(id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [tickets, assignedTickets, searchResults]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchTicketViews = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        if (isSubscribed) {
          setTicketViews([]);
          setActiveViewId(null);
        }
        return;
      }

      try {
        const response = await axios.get('/api/tickets/ticket-views', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!isSubscribed) {
          return;
        }

        const viewsData = Array.isArray(response.data) ? response.data : [];
        setTicketViews(viewsData);

        const activeTabParam = searchParams.get('activeTab');
        if (activeTabParam && activeTabParam.startsWith('view:')) {
          const viewIdFromParam = activeTabParam.replace('view:', '');
          const matchedView = viewsData.find((view) => String(getViewId(view)) === viewIdFromParam);
          if (matchedView) {
            // Notify filters: restoring a view without its query showed the
            // Général data under the view's name (browser-back / deep link).
            handleViewTabChange(matchedView, { skipUrlUpdate: true, suppressSyncFlag: true });
            return;
          }
        }

        if (viewsData.length === 0) {
          setActiveViewId(null);
          if (activeTopicTab && activeTopicTab.startsWith('view:')) {
            handleTopicTabChange(GENERAL_TAB);
          }
          return;
        }

        if (activeTopicTab && activeTopicTab.startsWith('view:')) {
          const currentViewId = activeTopicTab.replace('view:', '');
          const stillExists = viewsData.some((view) => String(getViewId(view)) === currentViewId);
          if (stillExists) {
            setActiveViewId(currentViewId);
            return;
          }
          // A view deleted since (e.g. someone's default view): back to Général.
          handleTopicTabChange(GENERAL_TAB);
          return;
        }

      } catch (error) {
        console.error('Failed to fetch ticket views:', error);
        if (isSubscribed) {
          setTicketViews([]);
          setActiveViewId(null);
          if (activeTopicTab && activeTopicTab.startsWith('view:')) {
            handleTopicTabChange(GENERAL_TAB);
          }
        }
      }
    };

    fetchTicketViews();

    return () => {
      isSubscribed = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  useEffect(() => {
    let alive = true;
    fetchPreferences().then((loaded) => { if (alive) setPreferences(loaded); });
    return () => { alive = false; };
  }, []);

  const sortTickets = (list, field, order) => {
    return [...list].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (field === 'updated_at' || field === 'created_at') {
        return order === 'asc' ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
      }
      if (field === 'sentiment_score') {
        // Missing scores always sink to the bottom, whatever the direction
        const numA = Number.isFinite(Number(valA)) && valA !== null ? Number(valA) : (order === 'asc' ? Infinity : -Infinity);
        const numB = Number.isFinite(Number(valB)) && valB !== null ? Number(valB) : (order === 'asc' ? Infinity : -Infinity);
        return order === 'asc' ? numA - numB : numB - numA;
      }
      const strA = String(valA ?? '');
      const strB = String(valB ?? '');
      return order === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
  };

  const filterTickets = (list) => {
    // Backend handles topic and assigned separation; brand stays frontend
    // until the backend supports it. Brand ids are ObjectId strings — compare
    // as strings so a numeric-looking id can never silently disable the
    // filter. Tickets without a brand_id pass (hiding them would empty every
    // brand view of unmapped tickets).
    const brandMatch = (ticket) => !selectedBrand
      || !ticket.brand_id
      || String(ticket.brand_id) === String(selectedBrand);

    if (searchResults !== null) {
      return searchResults.filter(brandMatch);
    }

    return list.filter(brandMatch);
  };

  // Sorting is server-side (KI-111): the parent re-queries page 1 with the
  // new order, and an active search re-runs with it too.
  const applySort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    updateUrlParams({ sortField: field, sortOrder: order });
    if (onSortChange) {
      onSortChange({ sort_by: field, order });
    }
    if (searchResults !== null && lastSearchCriteriaRef.current) {
      performSearch(lastSearchCriteriaRef.current, { page: 1, sortBy: field, order });
    }
  };

  const handleSortChange = (field) => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    applySort(field, newSortOrder);
  };

  // The saved view the list shows (« view:<id> » tab), or null on Général.
  const viewIdOfTab = (tab) => (tab && tab.startsWith('view:') ? tab.slice('view:'.length) : null);
  // The view the last search ran in (29/10: the search applies the view).
  const lastSearchViewRef = useRef(null);

  // Search: query and/or statuses (+ the EDN admin filter), inside the
  // current saved view (backend view_id). options: { page, sortBy, order,
  // viewId } — paging and sort are server-side; viewId overrides the tab
  // while it is being switched.
  const performSearch = async (searchData = null, options = {}) => {
    const page = options.page || 1;
    const sortBy = options.sortBy || sortField;
    const order = options.order || sortOrder;
    const viewId = options.viewId !== undefined ? options.viewId : viewIdOfTab(activeTopicTab);
    const searchCriteria = { ...(searchData || { query: searchFilter, ticket_status: statusFilters }) };

    if (!Object.prototype.hasOwnProperty.call(searchCriteria, 'ticket_status') && statusFilters.length > 0) {
      searchCriteria.ticket_status = statusFilters;
    }
    if (!Object.prototype.hasOwnProperty.call(searchCriteria, 'status_unchanged_days') && dormantDays) {
      searchCriteria.status_unchanged_days = dormantDays;
    }
    if (isEdnCustomUser) {
      const hasAdminFilter = Object.prototype.hasOwnProperty.call(searchCriteria, 'affected_admin_name')
        || Object.prototype.hasOwnProperty.call(searchCriteria, 'affected_admin_email');
      if (!hasAdminFilter) {
        const trimmedAdminFilter = ednAdminFilter.trim();
        if (trimmedAdminFilter) {
          if (trimmedAdminFilter.includes('@')) {
            searchCriteria.affected_admin_email = trimmedAdminFilter;
          } else {
            searchCriteria.affected_admin_name = trimmedAdminFilter;
          }
        }
      }
    }

    // Remove empty search criteria
    const filteredCriteria = Object.entries(searchCriteria)
      .filter(([, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value && String(value).trim() !== '';
      })
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    if (Object.keys(filteredCriteria).length === 0) {
      setSearchResults(null);
      setSearchPagination(null);
      lastSearchCriteriaRef.current = null;
      lastSearchViewRef.current = null;
      if (onSearchStateChange) {
        onSearchStateChange(false);
      }
      return;
    }

    lastSearchCriteriaRef.current = filteredCriteria;
    lastSearchViewRef.current = viewId;

    try {
      const token = localStorage.getItem('access_token');
      const queryParams = new URLSearchParams();
      Object.entries(filteredCriteria).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => queryParams.append(key, item));
          return;
        }
        queryParams.append(key, value);
      });
      if (viewId) {
        queryParams.append('view_id', viewId);
      }
      queryParams.append('page', String(page));
      queryParams.append('limit', String(SEARCH_PAGE_SIZE));
      queryParams.append('sort_by', sortBy);
      queryParams.append('order', order);
      const response = await axios.get(`/api/tickets/tickets/search?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.tickets || []);
      setSearchPagination(response.data.pagination || null);
      if (onSearchStateChange) {
        onSearchStateChange(true);
      }
    } catch (error) {
      console.error('Error searching tickets:', error);
      setSearchResults([]);
      setSearchPagination(null);
      // Still notify that search is active even if failed
      if (onSearchStateChange) {
        onSearchStateChange(true);
      }
    }
  };

  const hasOtherCriteria = () => statusFilters.length > 0 || Boolean(dormantDays) || (isEdnCustomUser && ednAdminFilter.trim() !== '');

  // The query was emptied: back to the list, or to the statuses still applied
  // (they used to be dropped silently while their facet stayed on screen).
  const resetQuery = () => {
    if (hasOtherCriteria()) {
      performSearch({ query: '', ticket_status: statusFilters });
      return;
    }
    setSearchResults(null);
    setSearchPagination(null);
    lastSearchCriteriaRef.current = null;
    if (onSearchStateChange) {
      onSearchStateChange(false);
    }
  };

  const handleSearchFilterChange = (e) => {
    const searchTerm = e.target.value;
    setSearchFilter(searchTerm);
    updateUrlParams({ searchFilter: searchTerm });
    if (searchTerm.trim() === '' && searchFilter.trim() !== '') {
      resetQuery();
    }
  };

  const handleEdnAdminFilterChange = (e) => {
    const adminFilter = e.target.value;
    setEdnAdminFilter(adminFilter);
    updateUrlParams({ ednAdminFilter: adminFilter });
  };

  const handleEdnAdminFilterSubmit = () => {
    const trimmedAdminFilter = ednAdminFilter.trim();
    performSearch({
      query: searchFilter,
      ticket_status: statusFilters,
      affected_admin_name: trimmedAdminFilter && !trimmedAdminFilter.includes('@') ? trimmedAdminFilter : '',
      affected_admin_email: trimmedAdminFilter && trimmedAdminFilter.includes('@') ? trimmedAdminFilter : ''
    });
  };

  const handleEdnAdminFilterKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEdnAdminFilterSubmit();
    }
  };

  const handleSearchSubmit = async () => {
    if (searchFilter.trim() === '') {
      resetQuery();
      return;
    }
    await performSearch({ query: searchFilter, ticket_status: statusFilters });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const clearSearchFilter = () => {
    setSearchFilter('');
    updateUrlParams({ searchFilter: '' });
    resetQuery();
  };

  const clearEdnAdminFilter = () => {
    setEdnAdminFilter('');
    updateUrlParams({ ednAdminFilter: '' });
    performSearch({
      query: searchFilter,
      ticket_status: statusFilters,
      affected_admin_name: '',
      affected_admin_email: ''
    });
  };

  // The order the person chose for a status (or ORDER_ALL), most recently
  // updated first by default; the current sort until the preferences load.
  const preferredSort = (statusKey) => (preferences
    ? { field: 'updated_at', order: orderFor(preferences, statusKey) }
    : { field: sortField, order: sortOrder });

  // Back on the unfiltered lists, the parent re-queries them in that order.
  const syncParentSort = (sort, filterCleared) => {
    if (filterCleared && onSortChange && (sort.field !== sortField || sort.order !== sortOrder)) {
      onSortChange({ sort_by: sort.field, order: sort.order });
    }
  };

  // Status filter (tiles, « Statut » menu): the search endpoint lists every
  // ticket in those statuses. A status and an attention reason never apply
  // together (the search cannot narrow on a reason): picking one clears the other.
  // `dormant`: days for the pipeline's « dormants » (null clears it); left
  // out, the current one stays while some status is picked.
  const applyStatusFilters = (next, dormant) => {
    const sort = preferredSort(next.length === 1 ? next[0] : ORDER_ALL);
    const nextDormant = dormant !== undefined ? dormant : (next.length > 0 ? dormantDays : null);
    setStatusFilters(next);
    setDormantDays(nextDormant);
    setSortField(sort.field);
    setSortOrder(sort.order);
    updateUrlParams({ statusFilter: next, dormantDays: nextDormant, sortField: sort.field, sortOrder: sort.order });
    if (next.length > 0 && attentionReason && onAttentionReasonChange) {
      onAttentionReasonChange(null);
    }
    performSearch(
      { query: searchFilter, ticket_status: next, status_unchanged_days: nextDormant || '' },
      { sortBy: sort.field, order: sort.order },
    );
    syncParentSort(sort, next.length === 0 && !nextDormant);
  };

  const toggleStatusTile = (statuses) => {
    applyStatusFilters(sameSet(statusFilters, statuses) ? [] : statuses);
  };

  const toggleStatusValue = (value) => {
    applyStatusFilters(statusFilters.includes(value)
      ? statusFilters.filter((status) => status !== value)
      : [...statusFilters, value]);
  };

  // An attention reason picked above (DashboardInsights) clears the statuses.
  useEffect(() => {
    if (!attentionReason || (statusFilters.length === 0 && !dormantDays)) return;
    setStatusFilters([]);
    setDormantDays(null);
    updateUrlParams({ statusFilter: [], dormantDays: null });
    performSearch({ query: searchFilter, ticket_status: [], status_unchanged_days: '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attentionReason]);

  const currentOrderKey = () => (statusFilters.length === 1 ? statusFilters[0] : ORDER_ALL);

  // A preference just saved in « Affichage »: an order changed for what the
  // list shows now applies at once.
  const handlePreferencesSaved = (saved, update) => {
    setPreferences(saved);
    const changed = Object.keys(update?.ticket_order || {});
    if (changed.length === 0) return;
    const key = currentOrderKey();
    const ownOrder = (saved.ticket_order || {})[key];
    if (changed.includes(key) || (changed.includes(ORDER_ALL) && !ownOrder)) {
      const order = orderFor(saved, key);
      if (order !== sortOrder || sortField !== 'updated_at') applySort('updated_at', order);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="tq__sort-icon" aria-hidden="true" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="tq__sort-icon is-active" aria-hidden="true" />
      : <ArrowDown className="tq__sort-icon is-active" aria-hidden="true" />;
  };

  const getPresenceViewers = (ticket) => {
    if (!ticket?.presence_viewers) {
      return [];
    }
    return ticket.presence_viewers.filter((viewer) => viewer && viewer.viewer_id);
  };

  const getPresenceTooltip = (viewers) => {
    if (!viewers.length) {
      return '';
    }
    const viewerNames = viewers.map((viewer) => viewer.viewer_name || viewer.role || t('unknown', 'Inconnu'));
    return `${t('ticketViewedByLabel', 'Actuellement consulté par')}:\n${viewerNames.join('\n')}`;
  };

  const getTicketTopicLabel = (ticket) => {
    return ticket?.ticket_topic?.topic_full
      || ticket?.ticket_topic?.name
      || ticket?.topic_name
      || ticket?.metadata?.topic_full
      || ticket?.topic_category
      || ticket?.metadata?.topic
      || ticket?.topic
      || t('unknown', 'Inconnu');
  };

  const getAffectedAdminLabel = (ticket) => {
    return ticket?.metadata?.affected_admin_name
      || ticket?.affected_admin_name
      || ticket?.metadata?.affected_admin_email
      || ticket?.affected_admin_email
      || '-';
  };

  const getStatusLabel = (status) => {
    if (isEdnCustomUser) {
      return EDN_STATUS_OPTIONS.find((option) => option.value === status)?.label
        || getCanonicalStatusLabel(t, status);
    }
    return getCanonicalStatusLabel(t, status);
  };

  const statusOptions = isEdnCustomUser
    ? EDN_STATUS_OPTIONS
    : ALL_STATUS_MAPPING.map(({ value }) => ({ value, label: getStatusLabel(value) }));

  // Fixed-position tooltip: the table lives in an overflow-x container which
  // clips absolutely-positioned descendants (user report 2026-10-02).
  // position:fixed escapes every overflow context.
  const [presenceTooltip, setPresenceTooltip] = useState(null);
  // The ticket whose « Résumé » overlay is open ({ id, subject }) — 28/10.
  const [summaryFor, setSummaryFor] = useState(null);

  const renderPresenceIndicator = (ticket) => {
    const viewers = getPresenceViewers(ticket);
    if (!viewers.length) {
      return null;
    }

    const tooltip = getPresenceTooltip(viewers);

    const showTooltip = (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setPresenceTooltip({ text: tooltip, x: rect.right, y: rect.bottom + 6 });
    };
    const hideTooltip = () => setPresenceTooltip(null);

    return (
      <span
        className="presence-indicator tq__presence"
        onClick={(event) => event.stopPropagation()}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        tabIndex={0}
        aria-label={tooltip}
      >
        <Eye aria-hidden="true" />
        <span>{viewers.length}</span>
      </span>
    );
  };

  // The status cell: the status badge, and under it who holds the ticket and
  // since when (`meta`: { node, text } — the holder, or EDN's admin, then
  // « depuis 26 h »). An assigned ticket adds the assignee's initials, and
  // hovering the cell tells who it is assigned to, by whom, when and why
  // (28/10), in the same fixed-position tooltip as the presence indicator.
  // A label too long for one line wraps inside its badge (never « … »); the
  // second line then has no room left and the layout drops it whole
  // (.tq__status-cell) — its text is always in the title / tooltip too.
  const renderStatusCell = (ticket, statusLabel, meta, waitTitle) => {
    const tone = statusTone(ticket.status);
    // Only an assignment made in Unanimity marks the row as assigned (Kevin
    // 04/11): an external system's admin copied by the sync (EDN « affecté
    // à », source « external ») is the « who » shown under the status.
    const assigned = Boolean(ticket.assignment_reason) && ticket.assignment_reason.source !== 'external';
    const name = assigned ? assigneeName(ticket) : null;
    const details = [meta.text, waitTitle].filter(Boolean).join('\n');
    const lines = (
      <span className="tq__status-cell" title={assigned ? undefined : [statusLabel, details].join('\n')}>
        <span className="tq__status-line">
          <span className={`u-badge${tone ? ` u-badge--${tone}` : ''} tq__status`}>
            <span className="u-badge__dot" aria-hidden="true" />
            <span className="tq__status-label">{statusLabel}</span>
          </span>
        </span>
        <span className="tq__secondary tq__status-meta">
          {assigned && (
            <span className="tq__assignee" aria-hidden="true">
              {name ? initialsOf(name) : <UserRound />}
            </span>
          )}
          {meta.node}
        </span>
      </span>
    );
    if (!assigned) {
      return lines;
    }
    const text = [assignmentTooltipText(t, ticket), details].filter(Boolean).join('\n');
    const showTooltip = (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setPresenceTooltip({ text, x: rect.left, y: rect.bottom + 6, align: 'start' });
    };
    const hideTooltip = () => setPresenceTooltip(null);
    return (
      <span
        className="tq__status-assignee"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        tabIndex={0}
        aria-label={`${statusLabel} · ${text}`}
        data-testid="status-assignee"
      >
        {lines}
      </span>
    );
  };

  useEffect(() => {
    if (hasSyncedInitialTab) {
      return;
    }

    if (!activeTopicTab) {
      return;
    }

    if (activeTopicTab.startsWith('view:')) {
      if (!ticketViews.length) {
        return;
      }

      const currentViewId = activeTopicTab.replace('view:', '');
      const matchedView = ticketViews.find((view) => String(getViewId(view)) === currentViewId);

      if (matchedView) {
        handleViewTabChange(matchedView, { skipUrlUpdate: true });
      } else {
        handleTopicTabChange(GENERAL_TAB, { skipUrlUpdate: true });
      }

      return;
    }

    handleTopicTabChange(activeTopicTab, { skipUrlUpdate: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopicTab, ticketViews, hasSyncedInitialTab]);

  // View-specific filters are explicitly cleared on every queue tab.
  const CLEARED_VIEW_FILTERS = {
    statuses_to_exclude: [],
    resolution_actors_to_exclude: [],
    channel_ids: [],
    employee_ids: []
  };

  const buildTopicFilters = (topic) => {
    if (topic === ASSIGNED_TAB) {
      return { topics: [], separate_assigned: true, viewMode: 'assigned', viewId: null, is_vip_only: false, ...CLEARED_VIEW_FILTERS };
    }
    if (topic === MY_ASSIGNED_TAB) {
      return { topics: [], separate_assigned: true, viewMode: 'my_assigned', viewId: null, is_vip_only: false, ...CLEARED_VIEW_FILTERS };
    }
    if (topic === CHATBOT_TAB) {
      return { topics: ['chatbot'], separate_assigned: true, viewMode: 'unassigned', viewId: null, is_vip_only: false, ...CLEARED_VIEW_FILTERS };
    }
    if (topic === VIP_TAB) {
      return { topics: [], separate_assigned: true, viewMode: 'unassigned', viewId: null, is_vip_only: true, ...CLEARED_VIEW_FILTERS };
    }
    return {
      topics: topic === GENERAL_TAB ? [] : [topic],
      separate_assigned: true,
      viewMode: 'unassigned',
      viewId: null,
      is_vip_only: false,
      ...CLEARED_VIEW_FILTERS
    };
  };

  const notifyFiltersForTopic = (topic, options = {}) => {
    if (!onFilterChange || options.skipFilterNotification) {
      return;
    }
    onFilterChange(buildTopicFilters(topic));
  };

  const buildViewFilters = (view) => {
    const viewId = getViewId(view);
    const viewFilters = view?.filters || {};

    return {
      separate_assigned: true,
      viewMode: 'view',
      viewId,
      topics: Array.isArray(viewFilters.topics) ? viewFilters.topics : [],
      statuses_to_exclude: Array.isArray(viewFilters.statuses_to_exclude) ? viewFilters.statuses_to_exclude : [],
      resolution_actors_to_exclude: Array.isArray(viewFilters.resolution_actors_to_exclude)
        ? viewFilters.resolution_actors_to_exclude
        : [],
      channel_ids: Array.isArray(viewFilters.channel_ids) ? viewFilters.channel_ids : [],
      employee_ids: Array.isArray(viewFilters.employee_ids) ? viewFilters.employee_ids : []
    };
  };

  const notifyFiltersForView = (view, options = {}) => {
    if (!onFilterChange || options.skipFilterNotification) {
      return;
    }
    onFilterChange(buildViewFilters(view));
  };

  // A search on screen follows the view: switching to another view (or back
  // to Général) runs it again there.
  const rerunSearchInView = (viewId) => {
    if (searchResults === null || !lastSearchCriteriaRef.current) return;
    if ((lastSearchViewRef.current || null) === (viewId || null)) return;
    performSearch(lastSearchCriteriaRef.current, { viewId: viewId || null });
  };

  const handleTopicTabChange = (topic, options = {}) => {
    setActiveTopicTab(topic);
    setActiveViewId(null);
    rerunSearchInView(null);

    if (!options.skipUrlUpdate) {
      updateUrlParams({ activeTab: topic });
    }

    if (!options.suppressSyncFlag) {
      setHasSyncedInitialTab(true);
    }

    notifyFiltersForTopic(topic, options);
  };

  const handleViewTabChange = (view, options = {}) => {
    const viewId = getViewId(view);
    if (!viewId) {
      console.warn('Skipping view tab activation: missing identifier', view);
      return;
    }

    const tabKey = buildViewTabKey(viewId);
    setActiveTopicTab(tabKey);
    setActiveViewId(String(viewId));
    rerunSearchInView(String(viewId));

    if (!options.skipUrlUpdate) {
      updateUrlParams({ activeTab: tabKey });
    }

    if (!options.suppressSyncFlag) {
      setHasSyncedInitialTab(true);
    }

    notifyFiltersForView(view, options);
  };

  const handleBrandChange = (brandId) => {
    if (onBrandChange) {
      onBrandChange(brandId);
    }
  };

  // Get the current tickets based on view mode
  const getCurrentTickets = () => {
    // If a view or the attention facet is active, merge both lists
    if (isMergedView) {
      return [...(tickets || []), ...(assignedTickets || [])];
    }
    if (currentViewMode === 'assigned' || currentViewMode === 'my_assigned') {
      return assignedTickets || []; // my_assigned uses the assignedTickets slot
    }
    return tickets || [];
  };

  // Get the current pagination based on view mode
  const getCurrentPagination = () => {
    if (isMergedView) {
      const currentPage = Math.max(pagination?.current_page ?? 0, assignedPagination?.current_page ?? 0, 1);
      const totalPages = Math.max(pagination?.total_pages ?? 0, assignedPagination?.total_pages ?? 0, 1);
      const totals = [pagination?.total_tickets, assignedPagination?.total_tickets].filter(isCount);
      return {
        current_page: currentPage,
        total_pages: totalPages,
        // Both lists are merged on screen, so are their totals.
        total_tickets: totals.length ? totals.reduce((sum, value) => sum + value, 0) : undefined,
        has_next: Boolean(pagination?.has_next || assignedPagination?.has_next),
        has_previous: Boolean(pagination?.has_previous || assignedPagination?.has_previous)
      };
    }

    if (currentViewMode === 'assigned' || currentViewMode === 'my_assigned') {
      return assignedPagination;
    }

    return pagination;
  };

  const displayedTickets = searchResults !== null ? searchResults : getCurrentTickets();
  // Rows arrive in server order (KI-111). The one exception is a saved view,
  // which merges the unassigned and assigned pages client-side: re-sorting the
  // merged array keeps that page coherent, but a view is only sorted per page
  // (two independently paged lists cannot be globally interleaved).
  const filteredTickets = filterTickets(displayedTickets);
  const sortedAndFilteredTickets = isMergedView && searchResults === null
    ? sortTickets(filteredTickets, sortField, sortOrder)
    : filteredTickets;

  const handlePageChange = (direction) => {
    if (searchResults !== null) {
      const current = searchPagination?.page || 1;
      if (direction === 'next' && searchPagination?.has_next) {
        performSearch(lastSearchCriteriaRef.current, { page: current + 1 });
      } else if (direction === 'prev' && searchPagination?.has_previous) {
        performSearch(lastSearchCriteriaRef.current, { page: Math.max(1, current - 1) });
      }
      return;
    }

    if (isMergedView) {
      const currentUnassigned = pagination?.current_page ?? 1;
      const currentAssigned = assignedPagination?.current_page ?? 1;

      const nextUnassigned = pagination
        ? direction === 'next'
          ? (pagination.has_next ? currentUnassigned + 1 : currentUnassigned)
          : (direction === 'prev' && pagination.has_previous ? Math.max(1, currentUnassigned - 1) : currentUnassigned)
        : null;

      const nextAssigned = assignedPagination
        ? direction === 'next'
          ? (assignedPagination.has_next ? currentAssigned + 1 : currentAssigned)
          : (direction === 'prev' && assignedPagination.has_previous ? Math.max(1, currentAssigned - 1) : currentAssigned)
        : null;

      const unassignedChanged = pagination ? nextUnassigned !== currentUnassigned : false;
      const assignedChanged = assignedPagination ? nextAssigned !== currentAssigned : false;

      if (unassignedChanged || assignedChanged) {
        onPageChange(
          unassignedChanged ? nextUnassigned : null,
          assignedChanged ? nextAssigned : null
        );
      }
      return;
    }

    if (currentViewMode === 'assigned' || currentViewMode === 'my_assigned') {
      const currentAssignedPage = assignedPagination?.current_page || 1;
      if (direction === 'next' && assignedPagination?.has_next) {
        onPageChange(null, currentAssignedPage + 1);
      } else if (direction === 'prev' && assignedPagination?.has_previous) {
        onPageChange(null, Math.max(1, currentAssignedPage - 1));
      }
      return;
    }

    const currentUnassignedPage = pagination?.current_page || 1;
    if (direction === 'next' && pagination?.has_next) {
      onPageChange(currentUnassignedPage + 1, null);
    } else if (direction === 'prev' && pagination?.has_previous) {
      onPageChange(Math.max(1, currentUnassignedPage - 1), null);
    }
  };

  // Update URL parameters when filters change
  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        newParams.delete(key);
        value.forEach((item) => {
          if (item !== '' && item !== null && item !== undefined) {
            newParams.append(key, item.toString());
          }
        });
        return;
      }

      if (value === '' || value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value.toString());
      }
    });

    setSearchParams(newParams, { replace: true });
  };

  // Search from the URL on mount (deep link, back from a ticket).
  useEffect(() => {
    const hasSimpleSearchParam = searchParams.get('searchFilter') && searchParams.get('searchFilter').trim() !== '';
    const statusParams = searchParams.getAll('statusFilter').filter(Boolean);
    const hasEdnAdminFilter = searchParams.get('ednAdminFilter') && searchParams.get('ednAdminFilter').trim() !== '';

    if (hasSimpleSearchParam || statusParams.length > 0 || (isEdnCustomUser && hasEdnAdminFilter)) {
      performSearch({
        query: searchParams.get('searchFilter') || '',
        ticket_status: statusParams
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // ── Row helpers ─────────────────────────────────────────────────────────

  // Who holds the next move, from the ticket status (coarse but honest; the
  // precise work state lives in the row's attention flags).
  const holderForStatus = (status) => {
    if (!status) return null;
    if (status === 'waiting_client_response') return 'customer';
    if (['waiting_carrier_response', 'waiting_carrier_commercial_response', 'waiting_carriers_response'].includes(status)) return 'carrier';
    if (['automated', 'resolved', 'ai_resolved', 'resolved_npec', 'closed'].includes(status)) return null;
    return 'team';
  };

  const HOLDER_LABELS = {
    team: t('holderTeam', 'Équipe'),
    customer: t('holderCustomer', 'Client'),
    carrier: t('holderCarrier', 'Transporteur'),
  };

  const formatDurationSince = (dateString) => {
    const since = parseUtc(dateString);
    if (!since) return '';
    const mins = Math.max(0, Math.round((Date.now() - since.getTime()) / 60000));
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 48) return `${hours} h`;
    return `${Math.floor(hours / 24)} j`;
  };

  // The company, else a name read from the e-mail (TicketListsShared); in FA
  // mode never company_name, never the backend's « Unknown ».
  const clientCellFor = (ticket) => {
    const { name, email } = clientIdentity(ticket, { hasFaAccount });
    if (!name) {
      // Chatbot / web-widget visitors have no identity beyond the session.
      return {
        visitor: true,
        primary: t('chatbotVisitor', 'Visiteur chatbot'),
        secondary: t('sessionShort', 'session #{{id}}', { id: String(ticket._id || '').slice(-4) }),
      };
    }
    return { primary: name, secondary: email };
  };

  // FA accounts sort every ticket into a segment (pre-sales, post-sales,
  // chatbot…): the second line of the subject, like EDN's motif. KI-321: a
  // ticket without a topic reads « Sans sujet », never « Après-vente ».
  const faTopicLabel = (ticket) => topicBadge(ticket.topic, t).label;

  // The subject's second line: the motif (EDN), the pre/post-sales class
  // (FA), then the e-mail's own subject when the AI renamed the ticket.
  const subjectSecondaryFor = (ticket) => {
    const original = (ticket.metadata || {}).original_subject;
    const originalShown = original && original !== ticket.subject ? original : null;
    let topic = null;
    if (isEdnCustomUser) {
      const label = getTicketTopicLabel(ticket);
      topic = label && label !== t('unknown', 'Inconnu') ? label : null;
    } else if (hasFaAccount) {
      topic = faTopicLabel(ticket);
    }
    const text = [topic, originalShown].filter(Boolean).join(' · ');
    const title = [
      topic && `${t('topicColumn', 'Motif')} : ${topic}`,
      originalShown && `${t('originalSubject', "Objet d'origine")} : ${originalShown}`,
    ].filter(Boolean).join('\n');
    return text ? { text, title } : null;
  };

  // ── Bulk + row actions (existing single-ticket endpoints, fanned out) ──

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

  const ensureEmployees = async () => {
    if (employeesLoaded) return;
    try {
      const response = await axios.get('/api/employees/agents/basic', authHeaders());
      setEmployees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      setEmployees([]);
    }
    setEmployeesLoaded(true);
  };

  const notifyResult = (ok, ko) => {
    if (onNotify) onNotify(t('bulkDone', '{{ok}} réussis · {{ko}} échecs', { ok, ko }), ko === 0);
  };

  const runBulk = async (ids, runOne) => {
    setBulkBusy(true);
    const results = await Promise.allSettled(ids.map(runOne));
    const ok = results.filter((result) => result.status === 'fulfilled').length;
    notifyResult(ok, results.length - ok);
    setBulkBusy(false);
    setSelectedIds([]);
    setAssignMenu(null);
    if (onRefresh) onRefresh();
  };

  const assignTickets = (ids, employeeId) => runBulk(ids, (ticketId) =>
    axios.post('/api/employees/assign-employee', { employee_id: employeeId, ticket_id: ticketId }, authHeaders()));

  // « Clore » is « Résoudre » since 2026-10-12: a closed ticket is a resolved one.
  const resolveTickets = (ids) => {
    setResolveIds(null);
    runBulk(ids, (ticketId) => axios.put(`/api/tickets/${ticketId}/resolve`, null, authHeaders()));
  };

  const toggleSelected = (ticketId) => {
    setSelectedIds((prev) => (prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]));
  };

  const openAssignMenu = (event, ids) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    if (assignMenu && sameSet(assignMenu.ids, ids)) {
      setAssignMenu(null);
      return;
    }
    ensureEmployees();
    const menuWidth = 240;
    setAssignMenu({
      ids,
      x: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
      y: rect.bottom + 6,
    });
  };

  const canAct = user?.role !== 'experience';
  const visibleIds = sortedAndFilteredTickets.map((ticket) => ticket._id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : visibleIds);
  };

  // ── What the card shows above the rows ──────────────────────────────────

  const currentPagination = searchResults !== null ? null : getCurrentPagination();
  const listTotal = searchResults !== null
    ? (searchPagination?.total_tickets ?? searchResults.length)
    : (isCount(currentPagination?.total_tickets) ? currentPagination.total_tickets : null);

  // Counts on the assignment filter: only those the last list answer gave.
  const queueCounts = (() => {
    if (searchResults !== null || isMergedView) return {};
    if (activeTopicTab === GENERAL_TAB || activeTopicTab === ASSIGNED_TAB) {
      return {
        [GENERAL_TAB]: isCount(pagination?.total_tickets) ? pagination.total_tickets : undefined,
        [ASSIGNED_TAB]: isCount(assignedPagination?.total_tickets) ? assignedPagination.total_tickets : undefined,
      };
    }
    if (activeTopicTab === MY_ASSIGNED_TAB) {
      return { [MY_ASSIGNED_TAB]: isCount(assignedPagination?.total_tickets) ? assignedPagination.total_tickets : undefined };
    }
    if (activeTopicTab === VIP_TAB) {
      return { [VIP_TAB]: isCount(pagination?.total_tickets) ? pagination.total_tickets : undefined };
    }
    return {};
  })();

  const queueOptions = [
    { value: GENERAL_TAB, label: tl('queue.unassigned', 'Non assignés'), count: queueCounts[GENERAL_TAB] },
    { value: ASSIGNED_TAB, label: t('queueAssigned', 'Assignés'), count: queueCounts[ASSIGNED_TAB] },
    { value: MY_ASSIGNED_TAB, label: t('queueMine', 'À moi'), count: queueCounts[MY_ASSIGNED_TAB] },
    ...(hasFaAccount ? [{ value: VIP_TAB, label: t('vip', 'VIP'), count: queueCounts[VIP_TAB] }] : []),
  ].map((option) => ({ ...option, count: isCount(option.count) ? fmtFr(option.count) : undefined }));

  const viewTabs = [
    { key: 'general', label: t('general', 'Général') },
    ...ticketViews.map((view) => {
      const viewId = getViewId(view);
      return viewId ? { key: buildViewTabKey(viewId), label: view.name } : null;
    }).filter(Boolean),
  ];
  const activeViewTab = activeTopicTab.startsWith('view:') ? activeTopicTab : 'general';
  const handleViewTabSelect = (key) => {
    if (key === 'general') {
      handleTopicTabChange(GENERAL_TAB);
      return;
    }
    const view = ticketViews.find((candidate) => buildViewTabKey(getViewId(candidate)) === key);
    if (view) handleViewTabChange(view);
  };

  // A topic tab reached by a deep link (?activeTab=chatbot or a topic name).
  const topicFacet = !activeTopicTab.startsWith('view:') && !QUEUE_TABS.includes(activeTopicTab)
    ? (activeTopicTab === CHATBOT_TAB ? t('chatbot', 'Chatbot') : activeTopicTab)
    : null;

  const tileLabels = {
    new: tl('tiles.new', 'Nouveaux'),
    client: tl('tiles.client', 'Attente client'),
    carrier: tl('tiles.carrier', 'Attente transporteur'),
    pending: tl('tiles.pending', 'En attente'),
  };
  const matchingTile = STATUS_TILES.find((tile) => sameSet(tile.statuses, statusFilters));
  let statusFacetLabel = null;
  if (statusFilters.length === 1) statusFacetLabel = getStatusLabel(statusFilters[0]);
  else if (statusFilters.length > 1) {
    statusFacetLabel = matchingTile
      ? tileLabels[matchingTile.key]
      : tl('queue.statusCount', '{{count}} statuts', { count: statusFilters.length });
  }

  const hasFacets = Boolean(attentionReason || statusFacetLabel || topicFacet || (searchResults !== null && searchFilter.trim()));

  // The saved view a search runs in (its name for the card's scope line).
  const searchViewId = viewIdOfTab(activeTopicTab);
  const searchViewName = searchViewId
    ? ticketViews.find((view) => String(getViewId(view)) === searchViewId)?.name || null
    : null;

  // ── Status tiles ────────────────────────────────────────────────────────

  const openCount = isCount(ticketStats?.remaining_tickets) ? ticketStats.remaining_tickets : null;
  const statusTiles = ticketStats
    ? STATUS_TILES.filter((tile) => isCount(ticketStats[tile.field]) && (!tile.onlyWhenAny || ticketStats[tile.field] > 0))
    : [];

  const renderTile = ({ key, icon: Icon, tone, label, value, context, meter, active, onClick, title }) => (
    <button
      key={key}
      type="button"
      className={`u-kpi tq__tile${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
      title={title}
    >
      <span className={`u-icon-chip u-icon-chip--sm${tone ? ` u-icon-chip--${tone}` : ''}`} aria-hidden="true">
        <Icon strokeWidth={1.8} />
      </span>
      <span className="u-kpi__body">
        <span className="u-overline">{label}</span>
        <span className="u-kpi__value">{value}</span>
        {context && <span className="u-kpi__context">{context}</span>}
        {meter}
      </span>
    </button>
  );

  const renderTiles = () => {
    // Every open ticket by stage (insights pipeline) replaces the four
    // status tiles, which covered 4 statuses out of 20 (Kevin 29/10).
    if (pipeline !== null) {
      return (
        <DashboardPipeline
          pipeline={pipeline}
          activeStatuses={statusFilters}
          dormantActive={Boolean(dormantDays)}
          onSelect={({ statuses, dormant }) => applyStatusFilters(
            statuses,
            dormant && statuses.length ? pipeline.dormant_after_days : null,
          )}
        />
      );
    }
    if (statsLoading && !ticketStats) {
      return (
        <div className="u-kpi-row tq__tiles" aria-busy="true">
          {[0, 1, 2, 3].map((index) => <span key={index} className="u-skeleton tq__tile-skeleton" />)}
        </div>
      );
    }
    if (!ticketStats || (openCount === null && statusTiles.length === 0)) return null;
    return (
      <div className="u-kpi-row tq__tiles" role="group" aria-label={tl('tiles.label', 'Filtrer par statut')}>
        {openCount !== null && renderTile({
          key: 'all',
          icon: Inbox,
          label: tl('tiles.open', 'Tickets ouverts'),
          value: fmtFr(openCount),
          context: tl('tiles.openContext', 'hors résolus'),
          active: statusFilters.length === 0,
          onClick: () => { if (statusFilters.length) applyStatusFilters([]); },
          title: tl('tiles.openHint', 'Afficher la file sans filtre de statut'),
        })}
        {statusTiles.map((tile) => {
          const value = ticketStats[tile.field];
          const share = openCount ? Math.round((value / openCount) * 100) : null;
          return renderTile({
            key: tile.key,
            icon: tile.icon,
            tone: tile.tone,
            label: tileLabels[tile.key],
            value: fmtFr(value),
            context: share !== null ? tl('tiles.share', '{{pct}} % des ouverts', { pct: share }) : null,
            meter: share !== null
              ? <Meter value={value} max={openCount} tone={tile.tone === 'warning' ? 'warning' : undefined} label={tileLabels[tile.key]} />
              : null,
            active: sameSet(statusFilters, tile.statuses),
            onClick: () => toggleStatusTile(tile.statuses),
            title: tl('tiles.hint', 'Afficher les tickets « {{label}} »', { label: tileLabels[tile.key] }),
          });
        })}
      </div>
    );
  };

  // ── Footer ──────────────────────────────────────────────────────────────

  const renderPagination = () => {
    if (searchResults !== null) {
      const totalResults = searchPagination?.total_tickets ?? searchResults.length;
      const totalPages = searchPagination?.total_pages || 1;
      return (
        <div className="tq__pager">
          <span className="tq__pager-count">{t('searchResultsCount', '{{count}} résultats', { count: fmtFr(totalResults) })}</span>
          {totalPages > 1 && (
            <div className="u-pagination__pages">
              <button type="button" className="u-btn u-btn--secondary u-btn--icon u-btn--sm" onClick={() => handlePageChange('prev')}
                disabled={!searchPagination?.has_previous} aria-label={t('common.previousPage', 'Page précédente')} title={t('common.previousPage', 'Page précédente')}>
                <ChevronLeft strokeWidth={2} />
              </button>
              <span className="u-num">{t('paginationPageOf', 'Page {{page}} / {{pages}}', { page: searchPagination?.page || 1, pages: totalPages })}</span>
              <button type="button" className="u-btn u-btn--secondary u-btn--icon u-btn--sm" onClick={() => handlePageChange('next')}
                disabled={!searchPagination?.has_next} aria-label={t('common.nextPage', 'Page suivante')} title={t('common.nextPage', 'Page suivante')}>
                <ChevronRight strokeWidth={2} />
              </button>
            </div>
          )}
        </div>
      );
    }
    const current = getCurrentPagination();
    const totalTickets = isCount(current?.total_tickets) ? current.total_tickets : null;
    return (
      <div className="tq__pager">
        {totalTickets !== null && (
          <span className="tq__pager-count">{t('paginationTickets', '{{count}} tickets', { count: fmtFr(totalTickets) })}</span>
        )}
        <div className="u-pagination__pages">
          <button type="button" className="u-btn u-btn--secondary u-btn--icon u-btn--sm" onClick={() => handlePageChange('prev')}
            disabled={!current?.has_previous} aria-label={t('common.previousPage', 'Page précédente')} title={t('common.previousPage', 'Page précédente')}>
            <ChevronLeft strokeWidth={2} />
          </button>
          <span className="u-num">
            {t('paginationPageOf', 'Page {{page}} / {{pages}}', { page: current?.current_page || 1, pages: current?.total_pages || 1 })}
          </span>
          <button type="button" className="u-btn u-btn--secondary u-btn--icon u-btn--sm" onClick={() => handlePageChange('next')}
            disabled={!current?.has_next} aria-label={t('common.nextPage', 'Page suivante')} title={t('common.nextPage', 'Page suivante')}>
            <ChevronRight strokeWidth={2} />
          </button>
        </div>
      </div>
    );
  };

  // ── Table geometry (kit § Tableaux): fixed layout, explicit widths, the
  // subject takes the rest. A secondary fact sits on the second line of the
  // column it belongs to (29/10) — humeur under the client, motif under the
  // subject, who holds the ticket and since when (EDN: its admin) under the
  // status, the tracking number under the carrier — so every mode fits the
  // card without scrolling sideways (COLUMN_WIDTHS). ──
  const columns = [
    canAct && { key: 'check', width: COLUMN_WIDTHS.check },
    { key: 'client', width: COLUMN_WIDTHS.client, css: CLIENT_WIDTH_CSS },
    { key: 'subject', min: SUBJECT_MIN },
    { key: 'status', width: COLUMN_WIDTHS.status },
    { key: 'carrier', width: COLUMN_WIDTHS.carrier },
    canAct && { key: 'actions', width: COLUMN_WIDTHS.actions },
  ].filter(Boolean);
  const tableMinWidth = columns.reduce((sum, column) => sum + (column.width || column.min), 0);
  const columnCount = columns.length;

  const listIsEmpty = sortedAndFilteredTickets.length === 0;
  const showLoadingRows = listIsEmpty && isLoading && searchResults === null;
  const isPlainQueue = searchResults === null && !isMergedView && activeTopicTab === GENERAL_TAB;

  const subjectOf = (ticket) => ticket.subject || tl('table.noSubject', '(sans objet)');

  return (
    <div className={`tq${hasFaAccount ? ' tq--fa' : ''}${isEdnCustomUser ? ' tq--edn' : ''}`}>
      {presenceTooltip && (
        <div
          className={`presence-tooltip-fixed tq__tooltip${presenceTooltip.align === 'start' ? ' is-start' : ''}`}
          style={{ left: presenceTooltip.x, top: presenceTooltip.y }}
          role="tooltip"
        >
          {presenceTooltip.text}
        </div>
      )}
      {summaryFor && (
        <TicketSummaryOverlay
          ticketId={summaryFor.id}
          subject={summaryFor.subject}
          onClose={() => setSummaryFor(null)}
        />
      )}

      {renderTiles()}

      <section className="u-card tq__card">
        <div className="u-card__header tq__head">
          <div className="u-card__heading">
            <h2 className="u-card__title">
              {tl('queue.title', 'Tickets')}
              {listTotal !== null && <span className="u-badge">{fmtFr(listTotal)}</span>}
            </h2>
            {searchResults !== null && (
              <p className="u-card__desc">
                {searchViewName
                  ? t('listsPolishUi.search.scopeView', 'Recherche dans la vue « {{view}} » : l’assignation ne s’applique pas.', { view: searchViewName })
                  : t('listsPolishUi.search.scopeAll', 'Recherche dans tous les tickets du compte : l’assignation ne s’applique pas.')}
              </p>
            )}
          </div>
          {canAct && (
            <div className="u-card__actions">
              <DisplayPreferences
                views={ticketViews}
                statuses={statusOptions}
                preferences={preferences}
                getViewId={getViewId}
                onSaved={handlePreferencesSaved}
              />
              {canExportTickets && (
                <button
                  type="button"
                  onClick={() => setShowExportModal(true)}
                  className="u-btn u-btn--secondary u-btn--sm"
                  title={t('ticketExport.title', 'Exporter les tickets')}
                >
                  <Download aria-hidden="true" /> {t('ticketExport.exportButton', 'Exporter')}
                </button>
              )}
              {isEdnCustomUser && onCreateTicket && (
                <button type="button" onClick={onCreateTicket} className="u-btn u-btn--primary u-btn--sm">
                  <Plus aria-hidden="true" /> {t('createEdnTicket', 'Créer un ticket EDN')}
                </button>
              )}
            </div>
          )}
        </div>

        {canAct && ticketViews.length > 0 && (
          <div className="tq__views">
            <Tabs tabs={viewTabs} active={activeViewTab} onChange={handleViewTabSelect} label={tl('queue.views', 'Vues de la file')} />
            {canManageViews && (
              <Link to="/ticket-views-parameters" className="u-btn u-btn--ghost u-btn--sm tq__views-manage">
                <Settings2 aria-hidden="true" /> {tl('queue.manageViews', 'Gérer les vues')}
              </Link>
            )}
          </div>
        )}

        {canAct && (
          <div className="u-toolbar tq__toolbar">
            {!isMergedView && (
              <Segmented
                options={queueOptions}
                value={QUEUE_TABS.includes(activeTopicTab) ? activeTopicTab : null}
                onChange={(value) => handleTopicTabChange(value)}
                label={t('facetAssignment', 'Assignation')}
              />
            )}
            {brands.length > 0 && (
              <Segmented
                options={brands.map((brand) => ({ value: brand.id, label: brand.name }))}
                value={selectedBrand}
                onChange={handleBrandChange}
                label={t('headerShop', 'Boutique')}
              />
            )}
            <div className="tq__menu-anchor" ref={statusMenuRef}>
              <button
                type="button"
                className={`u-btn u-btn--secondary u-btn--sm${statusFilters.length ? ' tq__filter-on' : ''}`}
                aria-haspopup="true"
                aria-expanded={showStatusMenu}
                onClick={() => setShowStatusMenu((prev) => !prev)}
              >
                <ListFilter aria-hidden="true" />
                {t('facetStatus', 'Statut')}
                {statusFilters.length > 0 && <span className="tq__filter-count">{statusFilters.length}</span>}
                <ChevronDown aria-hidden="true" />
              </button>
              {showStatusMenu && (
                <div className="u-menu tq__status-menu" role="group" aria-label={t('facetStatus', 'Statut')}>
                  <div className="tq__status-menu-head">
                    <span className="u-overline">{t('facetStatus', 'Statut')}</span>
                    {statusFilters.length > 0 && (
                      <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => applyStatusFilters([])}>
                        {t('clear', 'Effacer')}
                      </button>
                    )}
                  </div>
                  <div className="tq__status-menu-list">
                    {statusOptions.map((option) => (
                      <label key={option.value} className="tq__status-option">
                        <input
                          type="checkbox"
                          checked={statusFilters.includes(option.value)}
                          onChange={() => toggleStatusValue(option.value)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {dormantDays && (
              <button
                type="button"
                className="u-btn u-btn--secondary u-btn--sm tq__filter-on"
                onClick={() => applyStatusFilters(statusFilters, null)}
                title={t('dashPipelineUi.dormantClear', 'Afficher aussi les tickets dont le statut a changé récemment')}
              >
                {t('dashPipelineUi.dormantChip', 'Statut inchangé > {{days}} j', { days: dormantDays })}
                <X aria-hidden="true" />
              </button>
            )}
            {isEdnCustomUser && (
              <div className="u-input-group u-input-group--lead tq__admin-filter">
                <UserRound className="u-input-group__icon" aria-hidden="true" />
                <input
                  id="edn-admin-filter"
                  type="text"
                  className="u-input"
                  value={ednAdminFilter}
                  onChange={handleEdnAdminFilterChange}
                  onKeyDown={handleEdnAdminFilterKeyDown}
                  onBlur={handleEdnAdminFilterSubmit}
                  placeholder={t('filterByAdmin', 'Filtrer par admin')}
                  aria-label={t('filterByAdmin', 'Filtrer par admin')}
                />
                {ednAdminFilter && (
                  <button type="button" className="u-input-group__btn" onClick={clearEdnAdminFilter}
                    title={tl('queue.clearAdmin', 'Effacer le filtre admin')} aria-label={tl('queue.clearAdmin', 'Effacer le filtre admin')}>
                    <X />
                  </button>
                )}
              </div>
            )}
            <span className="u-toolbar__spacer" />
            <div className="u-input-group u-input-group--lead tq__search">
              <Search className="u-input-group__icon" aria-hidden="true" />
              <input
                id="search-input"
                type="search"
                className="u-input"
                value={searchFilter}
                onChange={handleSearchFilterChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={t('searchQueuePlaceholder', 'Rechercher client, sujet, n° de suivi…')}
                aria-label={t('search', 'Rechercher')}
              />
              {searchFilter && (
                <button type="button" className="u-input-group__btn" onClick={clearSearchFilter}
                  title={t('clearSearch', 'Effacer la recherche')} aria-label={t('clearSearch', 'Effacer la recherche')}>
                  <X />
                </button>
              )}
            </div>
          </div>
        )}

        {canAct && hasFacets && (
          <div className="tq__facets">
            {attentionReason && (
              <span className="u-tag tq__facet">
                {t('facetAttention', 'À traiter')}&nbsp;: {t(`dashboardInsights.flag_${attentionReason}`, attentionReason)}
                <button type="button" onClick={() => onAttentionReasonChange && onAttentionReasonChange(null)} aria-label={t('clear', 'Effacer')} title={t('clear', 'Effacer')}>
                  <X aria-hidden="true" />
                </button>
              </span>
            )}
            {statusFacetLabel && (
              <span className="u-tag tq__facet">
                {t('facetStatus', 'Statut')}&nbsp;: {statusFacetLabel}
                <button type="button" onClick={() => applyStatusFilters([])} aria-label={t('clear', 'Effacer')} title={t('clear', 'Effacer')}>
                  <X aria-hidden="true" />
                </button>
              </span>
            )}
            {topicFacet && (
              <span className="u-tag tq__facet">
                {tl('queue.topicFacet', 'Motif')}&nbsp;: {topicFacet}
                <button type="button" onClick={() => handleTopicTabChange(GENERAL_TAB)} aria-label={t('clear', 'Effacer')} title={t('clear', 'Effacer')}>
                  <X aria-hidden="true" />
                </button>
              </span>
            )}
            {searchResults !== null && searchFilter.trim() && (
              <span className="u-tag tq__facet">
                {tl('queue.searchFacet', 'Recherche')}&nbsp;: « {searchFilter.trim()} »
                <button type="button" onClick={clearSearchFilter} aria-label={t('clearSearch', 'Effacer la recherche')} title={t('clearSearch', 'Effacer la recherche')}>
                  <X aria-hidden="true" />
                </button>
              </span>
            )}
          </div>
        )}

        {canAct && selectedIds.length > 0 && (
          <div className="tq__bulk" role="region" aria-label={tl('bulk.label', 'Actions groupées')}>
            <span className="tq__bulk-count">{t('selectedCount', '{{count}} sélectionnés', { count: selectedIds.length })}</span>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--sm"
              disabled={bulkBusy}
              aria-haspopup="menu"
              aria-expanded={Boolean(assignMenu && sameSet(assignMenu.ids, selectedIds))}
              onClick={(event) => openAssignMenu(event, selectedIds)}
            >
              <UserPlus aria-hidden="true" />
              {t('assignTicket', 'Assigner')}
              <ChevronDown aria-hidden="true" />
            </button>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--sm"
              disabled={bulkBusy}
              onClick={() => setResolveIds(selectedIds)}
            >
              <CircleCheck aria-hidden="true" />
              {t('resolveTickets', 'Résoudre')}
            </button>
            <span className="u-toolbar__spacer" />
            <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setSelectedIds([])}>
              {tl('bulk.clear', 'Annuler la sélection')}
            </button>
          </div>
        )}

        <div className="u-table-scroll tq__scroll">
          <table className="u-table tq__table" style={{ minWidth: tableMinWidth }}>
            <colgroup>
              {columns.map((column) => <col key={column.key} style={column.width ? { width: column.css || column.width } : undefined} />)}
            </colgroup>
            <thead>
              <tr>
                {canAct && (
                  <th className="tq__check">
                    <input
                      type="checkbox"
                      className="tq__checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label={tl('bulk.selectAll', 'Tout sélectionner')}
                    />
                  </th>
                )}
                {/* A header names its column's first line, then its second
                    line after « · »; the sort sits on the fact it sorts by. */}
                <th aria-sort={sortField === 'sentiment_score' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <span className="tq__th">
                    <span>{t('customer', 'Client')}</span>
                    <span className="tq__th-sep" aria-hidden="true">·</span>
                    <button type="button" className="tq__sort" onClick={() => handleSortChange('sentiment_score')}
                      title={t('sentimentHint', "Sentiment estimé par l'IA d'après les messages (0–100)")}>
                      {t('sentimentScore', 'Humeur')} <SortIcon field="sentiment_score" />
                    </button>
                  </span>
                </th>
                <th>
                  <span className="tq__th">
                    <span>{t('subject', 'Sujet')}</span>
                    {(isEdnCustomUser || hasFaAccount) && (
                      <>
                        <span className="tq__th-sep" aria-hidden="true">·</span>
                        <span>{t('topicColumn', 'Motif')}</span>
                      </>
                    )}
                  </span>
                </th>
                <th aria-sort={sortField === 'updated_at' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <span className="tq__th">
                    <span>{t('status', 'Statut')}</span>
                    {isEdnCustomUser && (
                      <>
                        <span className="tq__th-sep" aria-hidden="true">·</span>
                        <span>{tl('table.admin', 'Admin')}</span>
                      </>
                    )}
                    <span className="tq__th-sep" aria-hidden="true">·</span>
                    <button type="button" className="tq__sort" onClick={() => handleSortChange('updated_at')}
                      title={tl('table.sortUpdated', 'Trier par dernière mise à jour')}>
                      {isEdnCustomUser ? t('listsPolishUi.table.since', 'Depuis') : t('waitingOnColumn', 'En attente de')}
                      <SortIcon field="updated_at" />
                    </button>
                  </span>
                </th>
                <th>{t('carrier', 'Transporteur')}</th>
                {canAct && <th aria-label={t('common.actions', 'Actions')} />}
              </tr>
            </thead>
            <tbody>
              {showLoadingRows && [0, 1, 2, 3, 4].map((index) => (
                <tr key={`loading-${index}`} className="tq__loading-row" aria-hidden="true">
                  <td colSpan={columnCount}><span className="u-skeleton tq__loading-bar" /></td>
                </tr>
              ))}
              {sortedAndFilteredTickets.map((ticket) => {
                const statusLabel = getStatusLabel(ticket.status);
                const affectedAdminLabel = isEdnCustomUser ? getAffectedAdminLabel(ticket) : '-';
                const attentionFlags = Array.isArray(ticket.attention_flags) ? ticket.attention_flags : [];
                const attentionTitle = attentionFlags
                  .map((flag) => t(`dashboardInsights.flag_${flag}`, flag))
                  .join(' · ');
                const client = clientCellFor(ticket);
                const holder = holderForStatus(ticket.status);
                const waitTitle = `${t('updatedAt', 'Maj le')} ${formatParisDateTime(ticket.updated_at)} · ${t('createdAtTooltip', 'Créé le {{date}}', { date: formatParisDateTime(ticket.created_at) })}`;
                const isSelected = selectedIds.includes(ticket._id);
                const subjectSecondary = subjectSecondaryFor(ticket);
                const carrierTitle = [ticket.carrier_name, ticket.tracking_number].filter(Boolean).join(' · ');
                const moodTitle = t('listsPolishUi.table.moodTitle', 'Humeur estimée par l’IA : {{score}}/100', {
                  score: Math.round(Number(ticket.sentiment_score)),
                });
                // Under the status: who holds the next move (EDN: the admin the
                // ticket is affected to — EDN's wording already says who it
                // waits for), then since when.
                const since = tl('table.since', 'depuis {{duration}}', { duration: formatDurationSince(ticket.updated_at) });
                const who = isEdnCustomUser
                  ? (affectedAdminLabel !== '-' ? affectedAdminLabel : null)
                  : (holder ? HOLDER_LABELS[holder] : null);
                const whoTitle = isEdnCustomUser
                  ? (who ? `${tl('table.admin', 'Admin')} : ${who}` : null)
                  : (who ? `${t('waitingOnColumn', 'En attente de')} : ${who}` : null);
                const statusMeta = {
                  text: [whoTitle, since].filter(Boolean).join(' · '),
                  node: (
                    <>
                      {who && <span className="tq__who">{who}</span>}
                      {who && <span className="tq__dot-sep" aria-hidden="true">·</span>}
                      <span className="tq__wait">{since}</span>
                    </>
                  ),
                };
                return (
                  <tr
                    key={ticket._id}
                    className={[
                      'tq__row',
                      isSelected ? 'is-selected' : '',
                      attentionFlags.length > 0 ? 'has-attention' : '',
                    ].filter(Boolean).join(' ')}
                    data-tour-demo={ticket.is_demo ? 'true' : undefined}
                    onClick={() => onTicketClick(ticket._id)}
                  >
                    {canAct && (
                      <td className="tq__check" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="tq__checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelected(ticket._id)}
                          aria-label={subjectOf(ticket)}
                        />
                      </td>
                    )}
                    <td title={[client.primary, client.secondary].filter(Boolean).join(' · ')}>
                      <div className="tq__person">
                        <span className="tq__avatar" aria-hidden="true">
                          {client.visitor ? <Bot strokeWidth={1.8} /> : initialsOf(client.primary)}
                        </span>
                        <div className="tq__lines">
                          <span className="tq__primary-row">
                            <span className="tq__primary">{client.primary}</span>
                            {ticket.is_vip && <span className="u-badge u-badge--accent tq__vip">{t('vip', 'VIP')}</span>}
                          </span>
                          <span className="tq__secondary">
                            <MoodInline
                              score={ticket.sentiment_score}
                              toneOf={moodTone}
                              title={moodTitle}
                              emptyTitle={t('noSatisfactionScore', 'Pas encore de score')}
                            />
                            <ChannelMark name={ticket.channel_name} />
                            {client.secondary}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="tq__subject">
                        {attentionFlags.length > 0 && (
                          <span className="tq__flag" title={attentionTitle} aria-label={attentionTitle} role="img">
                            <TriangleAlert strokeWidth={2} />
                          </span>
                        )}
                        <div className="tq__lines">
                          <button
                            type="button"
                            className="tq__subject-link"
                            title={subjectOf(ticket)}
                            onClick={(event) => { event.stopPropagation(); onTicketClick(ticket._id); }}
                          >
                            {subjectOf(ticket)}
                          </button>
                          {subjectSecondary && (
                            <span className="tq__secondary" title={subjectSecondary.title}>
                              {subjectSecondary.text}
                            </span>
                          )}
                        </div>
                        {renderPresenceIndicator(ticket)}
                      </div>
                    </td>
                    <td>{renderStatusCell(ticket, statusLabel, statusMeta, waitTitle)}</td>
                    <td title={carrierTitle || undefined}>
                      {ticket.carrier_name || ticket.tracking_number ? (
                        <div className="tq__lines">
                          {ticket.carrier_name
                            ? <span className="tq__primary tq__primary--plain">{ticket.carrier_name}</span>
                            : <span className="tq__primary tq__muted">—</span>}
                          {ticket.tracking_number && <span className="tq__secondary tq__tracking">{ticket.tracking_number}</span>}
                        </div>
                      ) : (
                        <span className="tq__muted">—</span>
                      )}
                    </td>
                    {canAct && (
                      <td className="tq__actions" onClick={(event) => event.stopPropagation()}>
                        <div className="tq__actions-inner">
                          <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                            onClick={() => { setPresenceTooltip(null); setSummaryFor({ id: ticket._id, subject: ticket.subject }); }}
                            title={t('ticketSummaryOverlay.open', 'Lire le résumé du ticket')}
                            aria-label={t('ticketSummaryOverlay.action', 'Résumé')}
                          >
                            <FileText strokeWidth={1.8} />
                          </button>
                          <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                            onClick={(event) => openAssignMenu(event, [ticket._id])}
                            aria-haspopup="menu"
                            aria-expanded={Boolean(assignMenu && sameSet(assignMenu.ids, [ticket._id]))}
                            title={tl('table.assignHint', 'Assigner à un membre de l’équipe')}
                            aria-label={t('assignTicket', 'Assigner')}
                          >
                            <UserPlus strokeWidth={1.8} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {showLoadingRows && (
          <span className="tq__sr-only" role="status">{t('loadingTicketsList', 'Chargement des tickets…')}</span>
        )}

        {listIsEmpty && !showLoadingRows && (
          <EmptyState
            icon={searchResults !== null ? Search : Inbox}
            title={searchResults !== null
              ? t('noSearchResults', 'Aucun ticket ne correspond à cette recherche.')
              : t('noTicketsInView', 'Aucun ticket dans cette vue.')}
            action={isPlainQueue && listTotal === 0 && openCount === 0 && canManageViews ? (
              <Link to="/parameters?tab=integrations" className="u-btn u-btn--secondary u-btn--sm">
                {tl('empty.connect', 'Vérifier les canaux connectés')}
              </Link>
            ) : null}
          >
            {isPlainQueue && openCount === 0
              ? tl('empty.noneYet', 'Les messages reçus sur vos canaux connectés deviennent des tickets et arrivent ici.')
              : null}
          </EmptyState>
        )}

        {(searchResults !== null || getCurrentPagination() || canAct) && (
          <div className="u-pagination tq__footer">
            <span className="tq__footer-note">
              {canAct && sortedAndFilteredTickets.length > 0
                ? `${tl('table.shown', '{{count}} affichés', { count: sortedAndFilteredTickets.length })} · ${t('bulkHintResolve', 'actions groupées : assigner, résoudre')}`
                : null}
            </span>
            {(searchResults !== null || getCurrentPagination()) && renderPagination()}
          </div>
        )}
      </section>

      {assignMenu && (
        <div
          ref={assignMenuRef}
          className="u-menu tq__assign-menu"
          role="menu"
          style={{ left: assignMenu.x, top: assignMenu.y }}
        >
          <div className="tq__assign-title u-overline">
            {assignMenu.ids.length > 1
              ? tl('assign.titleMany', 'Assigner {{count}} tickets à', { count: assignMenu.ids.length })
              : tl('assign.title', 'Assigner à')}
          </div>
          {!employeesLoaded && <span className="u-skeleton tq__assign-skeleton" aria-hidden="true" />}
          {employeesLoaded && employees.length === 0 && (
            <span className="tq__assign-empty">{tl('assign.empty', 'Aucun membre dans l’équipe.')}</span>
          )}
          {employees.map((employee) => {
            const employeeId = employee._id || employee.id;
            return (
              <button
                key={employeeId}
                type="button"
                role="menuitem"
                className="u-menu__item"
                disabled={bulkBusy}
                onClick={(event) => { event.stopPropagation(); assignTickets(assignMenu.ids, employeeId); }}
              >
                <span className="tq__avatar tq__avatar--small" aria-hidden="true">{initialsOf(employee.name || employeeId)}</span>
                {employee.name || employeeId}
              </button>
            );
          })}
        </div>
      )}

      {resolveIds && (
        <ConfirmDialog
          title={tl('bulk.resolveTitle', 'Résoudre {{count}} tickets ?', { count: resolveIds.length })}
          message={tl('bulk.resolveMessage', 'Ils quittent la file des tickets ouverts et passent dans les archives.')}
          confirmLabel={t('resolveTickets', 'Résoudre')}
          cancelLabel={t('common.cancel', 'Annuler')}
          icon={CircleCheck}
          busy={bulkBusy}
          onConfirm={() => resolveTickets(resolveIds)}
          onCancel={() => setResolveIds(null)}
        />
      )}

      {showExportModal && (
        <TicketExportModal onClose={() => setShowExportModal(false)} />
      )}
    </div>
  );
};

export default TicketTableDashboard;
